import React, { Fragment } from "react";
import { Divider, Popper } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Text } from "../Base";
import { View } from "../View";
import { palette } from "../../theme/palette";
import { groupBy } from "lodash";
import { CloseIcon } from "../Icons/Close";
import { WarningIcon } from "../Icons/Warning";
import { timestampToHHMM } from "../../utils/date";

const useStyles = makeStyles({
  popupContainer: {
    position: "fixed",
    top: 0,
    left: 0,
    height: "100vh",
    width: "100vw",
    background: "transparent",
    zIndex: "1400 !important",
  },
  arrowContainer: {
    marginLeft: "5px",
    marginTop: "10px",
  },
  arrow: {
    width: "0",
    height: "0",
    borderTop: "5px solid transparent",
    borderBottom: "5px solid transparent",
    borderRight: `10px solid ${palette.lowTranslucentTrueBlack}`,
  },
  popup: {
    display: "flex",
    color: palette.white,
    width: "245px",
    maxHeight: "280px",
    paddingBottom: "50px",
    paddingRight: "5px",
    zIndex: "1400 !important",
    borderColor: palette.typography.black.high,
    borderRadius: "10px",
  },
  popupContents: {
    width: "240px",
    maxHeight: "255px",
    background: palette.lowTranslucentTrueBlack,
    padding: "10px",
    borderRadius: "10px",
  },
  detailsContainerTopRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: "18px",
  },
  detailsContainerCloseIcon: {
    height: "12px",
    cursor: "pointer",
  },
  postcode: {
    fontSize: "19px",
  },
  route: {
    fontSize: "16px",
    marginTop: "10px",
    marginBottom: "0px",
    color: palette.sunburst,
  },
  overrideDetails: {
    display: "flex",
    marginBottom: "5px",
  },
  overrideListItem: {
    fontSize: "12px",
  },
  detailsContainerDivider: {
    background: palette.translucentWhite,
  },
  overrideList: {
    overflow: "scroll",
    margin: "0px",
    maxHeight: "240px",
  },
  iconContainer: {
    display: "flex",
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    marginLeft: "15px",
  },
  warningIcon: {
    width: "22px",
    height: "22px",
    fill: palette.dpdRed,
  },
});

export const OverridePopup = ({ anchorEl, onClose, overrides }) => {
  const styles = useStyles();

  const processOverrideList = () => {
    let overrideList = [];
    const groupedOverrides = groupBy(overrides, "routeCode");
    overrideList = Object.keys(groupedOverrides).map((routeCode) => {
      const overridesForRouteCode = groupedOverrides[routeCode];
      let overrideListForRouteCode = {
        routeCode,
        overrideLines: [],
      };

      overridesForRouteCode.forEach((overrideForRouteCode) => {
        overrideListForRouteCode.overrideLines = [
          ...overrideListForRouteCode.overrideLines,
          overrideForRouteCode,
        ];
      });

      return overrideListForRouteCode;
    });
    return overrideList;
  };

  const renderOverrideList = () => {
    const { outer, sector, postcode } = overrides[0];
    const areaKey = postcode || sector || outer;
    const overrideList = processOverrideList();
    const textForLevel = [
      <Text key={areaKey} className={styles.postcode}>
        {areaKey}
      </Text>,
      ...overrideList.flatMap(({ routeCode, overrideLines }) => [
        <Text
          key={`${postcode}-${routeCode}`}
          className={styles.route}
        >{`Route ${routeCode}`}</Text>,
        ...overrideLines.map((override) => {
          const {
            overrideUuid,
            uiOverrideStartTime,
            uiOverrideEndTime,
            startOperator,
            endOperator,
            conflict,
            excludeOneHour,
          } = override;

          return (
            <View
              key={`${postcode}-${routeCode}-${overrideUuid}`}
              className={styles.overrideDetails}
            >
              <View>
                {uiOverrideStartTime && (
                  <Text className={styles.overrideListItem}>
                    {`Service Window Start Time: ${startOperator} ${timestampToHHMM(
                      uiOverrideStartTime
                    )}`}
                  </Text>
                )}
                {uiOverrideEndTime && (
                  <Text className={styles.overrideListItem}>
                    {`Service Window End Time: ${endOperator} ${timestampToHHMM(
                      uiOverrideEndTime
                    )}`}
                  </Text>
                )}
                {excludeOneHour && (
                  <Text className={styles.overrideListItem}>
                    Exclude One Hour Windows
                  </Text>
                )}
              </View>
              {conflict && (
                <View className={styles.iconContainer}>
                  <WarningIcon className={styles.warningIcon} />
                </View>
              )}
            </View>
          );
        }),
      ]),
    ];
    return (
      <Fragment key={postcode}>
        <View className={styles.detailsTextList}>{textForLevel}</View>
        <Divider className={styles.detailsContainerDivider} light={true} />
      </Fragment>
    );
  };

  return (
    !!anchorEl && (
      <View
        className={styles.popupContainer}
        onWheel={onClose}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <Popper
          className={styles.popup}
          open={!!anchorEl}
          anchorEl={anchorEl}
          placement={"right-start"}
          onWheel={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <View className={styles.arrowContainer}>
            <View className={styles.arrow} />
          </View>
          <View className={styles.popupContents}>
            <View className={styles.detailsContainerTopRow}>
              <Text>Override</Text>
              <CloseIcon
                role="button"
                onClick={onClose}
                className={styles.detailsContainerCloseIcon}
              />
            </View>
            <Divider className={styles.detailsContainerDivider} light={true} />
            <View className={styles.overrideList}>{renderOverrideList()}</View>
          </View>
        </Popper>
      </View>
    )
  );
};
